import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Ruler, User, CheckCircle, AlertTriangle, Info, Calculator, DollarSign, Plus, Minus, Search, Layers, Package, Shirt, X, Shield, Scissors } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useFabricCatalog } from "@/contexts/FabricCatalogContext";

// ============================================
// PROFESSIONAL FABRIC ESTIMATION SYSTEM
// Based on pattern-piece calculations
// ============================================

// Base fabric requirements by garment and height category (150cm width)
const BASE_FABRIC = {
  "2pc-suit": {
    short: { min: 2.8, max: 3.0 },      // ≤170cm
    regular: { min: 3.0, max: 3.3 },    // 171-182cm
    tall: { min: 3.3, max: 3.6 },       // ≥183cm
    name: "2-Piece Suit",
    category: "suit"
  },
  "3pc-suit": {
    short: { min: 3.4, max: 3.7 },
    regular: { min: 3.6, max: 4.0 },
    tall: { min: 4.0, max: 4.4 },
    name: "3-Piece Suit",
    category: "suit",
    hasVest: true
  },
  "jacket": {
    short: { min: 1.5, max: 1.6 },
    regular: { min: 1.6, max: 1.8 },
    tall: { min: 1.9, max: 2.1 },
    name: "Jacket Only",
    category: "jacket"
  },
  "trousers": {
    short: { min: 1.1, max: 1.2 },
    regular: { min: 1.2, max: 1.3 },
    tall: { min: 1.3, max: 1.5 },
    name: "Trousers",
    category: "trousers"
  },
  "vest": {
    liningBack: { min: 0.40, max: 0.45 },
    selfBack: { min: 0.60, max: 0.70 },
    name: "Vest/Waistcoat",
    category: "vest"
  },
  "overcoat-short": {
    short: { min: 2.3, max: 2.5 },
    regular: { min: 2.4, max: 2.6 },
    tall: { min: 2.6, max: 2.8 },
    name: "Short Coat (Topcoat)",
    category: "coat"
  },
  "overcoat-knee": {
    short: { min: 2.8, max: 3.0 },
    regular: { min: 2.9, max: 3.2 },
    tall: { min: 3.2, max: 3.5 },
    name: "Knee-Length Coat",
    category: "coat"
  },
  "overcoat-full": {
    short: { min: 3.5, max: 3.8 },
    regular: { min: 3.7, max: 4.0 },
    tall: { min: 4.0, max: 4.5 },
    name: "Full-Length Coat",
    category: "coat"
  },
  "shirt": {
    short: { min: 1.3, max: 1.4 },
    regular: { min: 1.4, max: 1.6 },
    tall: { min: 1.6, max: 1.8 },
    name: "Shirt",
    category: "shirt"
  },
};

// Design add-ons (in meters)
const DESIGN_ADDONS = {
  doubleBreasted: { value: 0.25, label: "Double Breasted", applies: ["suit", "jacket", "coat"] },
  longJacket: { value: 0.25, label: "Long Jacket", applies: ["suit", "jacket"] },
  pleatedTrousers: { value: 0.15, label: "Pleated Trousers", applies: ["suit", "trousers"] },
  highRise: { value: 0.12, label: "High Rise", applies: ["suit", "trousers"] },
  cuffedTrousers: { value: 0.08, label: "Cuffed Trousers", applies: ["suit", "trousers"] },
  wideLeg: { value: 0.10, label: "Wide Leg", applies: ["suit", "trousers"] },
  vestDoubleBreasted: { value: 0.10, label: "Double Breasted Vest", applies: ["vest"] },
  raglanSleeve: { value: 0.15, label: "Raglan Sleeve", applies: ["coat"] },
  beltedBack: { value: 0.10, label: "Belted Back", applies: ["coat"] },
};

// Pattern multipliers
const PATTERN_MULTIPLIERS = {
  solid: { value: 1.0, label: "Solid / Plain", add: 0 },
  subtleStripe: { value: 1.08, label: "Subtle Stripe", add: 0.08 },
  stripe: { value: 1.12, label: "Bold Stripe", add: 0.12 },
  check: { value: 1.18, label: "Check / Plaid", add: 0.18 },
  largeCheck: { value: 1.25, label: "Large Check / Windowpane", add: 0.25 },
};

// Fabric type multipliers
const FABRIC_TYPE_MULTIPLIERS = {
  standard: { value: 1.0, label: "Standard Weave" },
  nap: { value: 1.12, label: "Napped / Velvet / Corduroy (+12%)" },
  delicate: { value: 1.08, label: "Delicate / Silk (+8%)" },
};

// Fabric width adjustments
const WIDTH_ADJUSTMENTS = {
  "140": { value: 0.20, label: "140 cm (Narrow)", multiplier: 1.15 },
  "150": { value: 0, label: "150 cm (Standard)", multiplier: 1.0 },
  "160": { value: -0.20, label: "160 cm (Wide)", multiplier: 0.92 },
};

// Body shape modifiers
const BODY_SHAPES = {
  lean: { jacket: 0, trousers: 0, label: "Lean", description: "Slim build" },
  athletic: { jacket: 0.08, trousers: 0.05, label: "Athletic", description: "Broad chest, narrow waist" },
  regular: { jacket: 0, trousers: 0, label: "Regular", description: "Balanced proportions" },
  heavyset: { jacket: 0.15, trousers: 0.15, label: "Heavy-set", description: "Waist ≈ chest" },
  portly: { jacket: 0.25, trousers: 0.25, label: "Portly / Full", description: "Waist > chest" },
};

// CMT Costs from Kutesmart (USD)
const CMT_COSTS = {
  "full-canvas-3pc": { name: "Full Canvas Three-piece Suit", cmt: 310 },
  "half-canvas-3pc": { name: "Half Canvas Three-piece Suit", cmt: 230 },
  "fused-canvas-3pc": { name: "Fused Canvas Three-piece Suit", cmt: 210 },
  "unstructured-3pc": { name: "Unstructured Three-piece Suit", cmt: 230 },
  "full-canvas-2pc": { name: "Full Canvas Suit (2 pcs)", cmt: 260 },
  "half-canvas-2pc": { name: "Half Canvas Suit (2 pcs)", cmt: 180 },
  "fused-canvas-2pc": { name: "Fused Canvas Suit (2 pcs)", cmt: 160 },
  "unstructured-2pc": { name: "Unstructured Suit (2 pcs)", cmt: 180 },
  "full-canvas-jacket": { name: "Full Canvas Jacket", cmt: 210 },
  "half-canvas-jacket": { name: "Half Canvas Jacket", cmt: 130 },
  "fused-canvas-jacket": { name: "Fused Canvas Jacket", cmt: 110 },
  "unstructured-jacket": { name: "Unstructured Jacket", cmt: 135 },
  "shirt": { name: "Shirt", cmt: 30 },
  "shirt-collar": { name: "Shirt + Collar upgrade", cmt: 40 },
  "shirt-cuff": { name: "Shirt + Cuff upgrade", cmt: 40 },
  "shirt-pocket": { name: "Shirt + Pocket upgrade", cmt: 40 },
  "pant": { name: "Pant", cmt: 50 },
  "dress-chino": { name: "Dress Chino", cmt: 50 },
  "bermuda": { name: "Bermuda", cmt: 50 },
  "jeans": { name: "Jeans", cmt: 40 },
  "casual-chinos": { name: "Casual Chinos", cmt: 40 },
  "vest": { name: "Vest", cmt: 50 },
  "full-canvas-overcoat": { name: "Full Canvas Overcoat", cmt: 250 },
  "half-canvas-overcoat": { name: "Half Canvas Overcoat", cmt: 180 },
  "fused-canvas-overcoat": { name: "Fused Canvas Overcoat", cmt: 150 },
  "handkerchief": { name: "Handkerchief", cmt: 10 },
};

const FREIGHT_COST = 50;
const TARIFF_COST = 30;
const BANK_FEE_RATE = 0.03;

// Helper functions
const getHeightCategory = (heightCm) => {
  if (heightCm <= 170) return "short";
  if (heightCm <= 182) return "regular";
  return "tall";
};

const getUniqueValues = (fabricData, category, field) => {
  const values = new Set();
  if (fabricData[category]) {
    fabricData[category].forEach(item => {
      if (item[field]) values.add(item[field]);
    });
  }
  return Array.from(values).sort();
};

const FabricEstimator = ({ onEstimateComplete, onPriceCalculated }) => {
  const { FABRIC_DATA } = useFabricCatalog();
  // ============================================
  // FABRIC ESTIMATOR STATE
  // ============================================
  const [garmentType, setGarmentType] = useState("");
  const [heightUnit, setHeightUnit] = useState("imperial");
  const [heightFeet, setHeightFeet] = useState("");
  const [heightInches, setHeightInches] = useState("");
  const [heightCm, setHeightCm] = useState("");
  
  // Design options
  const [jacketLength, setJacketLength] = useState("regular");
  const [breastStyle, setBreastStyle] = useState("single");
  const [trouserRise, setTrouserRise] = useState("regular");
  const [trouserLeg, setTrouserLeg] = useState("classic");
  const [trouserPleats, setTrouserPleats] = useState("flat");
  const [trouserCuffs, setTrouserCuffs] = useState("no");
  const [vestBack, setVestBack] = useState("lining");
  const [coatLength, setCoatLength] = useState("knee");
  
  // Fabric options
  const [pattern, setPattern] = useState("solid");
  const [fabricType, setFabricType] = useState("standard");
  const [fabricWidth, setFabricWidth] = useState("150");
  
  // Body shape
  const [bodyShape, setBodyShape] = useState("regular");
  
  // Safety mode
  const [safetyMode, setSafetyMode] = useState(false);
  const [shrinkageAllowance, setShrinkageAllowance] = useState(true);

  // ============================================
  // COST CALCULATOR STATE
  // ============================================
  const [cmtGarmentType, setCmtGarmentType] = useState("");
  const [fabricCost, setFabricCost] = useState("");
  const [fabricMeters, setFabricMeters] = useState("");
  const [markup, setMarkup] = useState("2.5");
  const [extras, setExtras] = useState([]);
  const [selectedFabric, setSelectedFabric] = useState(null);

  // Fabric catalog state
  const [showFabricDialog, setShowFabricDialog] = useState(false);
  const [fabricCategory, setFabricCategory] = useState("suits");
  const [fabricSearch, setFabricSearch] = useState("");
  const [fabricFilters, setFabricFilters] = useState({ composition: "all", pattern: "all", color: "all" });
  const [quickSearchCode, setQuickSearchCode] = useState("");

  // ============================================
  // CALCULATIONS
  // ============================================
  
  // Convert height to cm
  const getHeightCmValue = () => {
    if (heightUnit === "imperial") {
      const feet = parseFloat(heightFeet) || 0;
      const inches = parseFloat(heightInches) || 0;
      return (feet * 30.48) + (inches * 2.54);
    }
    return parseFloat(heightCm) || 0;
  };

  // Main fabric calculation
  const fabricCalculation = useMemo(() => {
    if (!garmentType) return null;
    
    const heightCmVal = getHeightCmValue();
    const heightCategory = heightCmVal > 0 ? getHeightCategory(heightCmVal) : "regular";
    
    let garmentKey = garmentType;
    let garmentData = BASE_FABRIC[garmentType];
    
    // Handle coat length selection
    if (garmentType === "overcoat") {
      garmentKey = `overcoat-${coatLength}`;
      garmentData = BASE_FABRIC[garmentKey];
    }
    
    if (!garmentData) return null;
    
    // Get base range
    let baseMin, baseMax;
    if (garmentType === "vest") {
      const vestType = vestBack === "self" ? "selfBack" : "liningBack";
      baseMin = garmentData[vestType].min;
      baseMax = garmentData[vestType].max;
    } else {
      baseMin = garmentData[heightCategory]?.min || garmentData.regular.min;
      baseMax = garmentData[heightCategory]?.max || garmentData.regular.max;
    }
    
    // Initialize adjustments tracking
    const adjustments = [];
    let addOnsTotal = 0;
    
    // Design add-ons
    const category = garmentData.category;
    
    // Breast style
    if (breastStyle === "double" && ["suit", "jacket", "coat"].includes(category)) {
      addOnsTotal += DESIGN_ADDONS.doubleBreasted.value;
      adjustments.push({ label: "Double Breasted", value: DESIGN_ADDONS.doubleBreasted.value });
    }
    
    // Jacket length
    if (jacketLength === "long" && ["suit", "jacket"].includes(category)) {
      addOnsTotal += DESIGN_ADDONS.longJacket.value;
      adjustments.push({ label: "Long Jacket", value: DESIGN_ADDONS.longJacket.value });
    }
    
    // Trouser options
    if (["suit", "trousers"].includes(category)) {
      if (trouserPleats === "pleated") {
        addOnsTotal += DESIGN_ADDONS.pleatedTrousers.value;
        adjustments.push({ label: "Pleated Trousers", value: DESIGN_ADDONS.pleatedTrousers.value });
      }
      if (trouserRise === "high") {
        addOnsTotal += DESIGN_ADDONS.highRise.value;
        adjustments.push({ label: "High Rise", value: DESIGN_ADDONS.highRise.value });
      }
      if (trouserCuffs === "yes") {
        addOnsTotal += DESIGN_ADDONS.cuffedTrousers.value;
        adjustments.push({ label: "Cuffed Trousers", value: DESIGN_ADDONS.cuffedTrousers.value });
      }
      if (trouserLeg === "wide") {
        addOnsTotal += DESIGN_ADDONS.wideLeg.value;
        adjustments.push({ label: "Wide Leg", value: DESIGN_ADDONS.wideLeg.value });
      }
    }
    
    // Vest options
    if (category === "vest" && breastStyle === "double") {
      addOnsTotal += DESIGN_ADDONS.vestDoubleBreasted.value;
      adjustments.push({ label: "Double Breasted Vest", value: DESIGN_ADDONS.vestDoubleBreasted.value });
    }
    
    // Coat options
    if (category === "coat") {
      // Raglan sleeve could be added here
    }
    
    // Apply add-ons to base
    let workingMin = baseMin + addOnsTotal;
    let workingMax = baseMax + addOnsTotal;
    
    // Pattern multiplier
    const patternData = PATTERN_MULTIPLIERS[pattern];
    if (patternData.value > 1) {
      const patternAddMin = workingMin * (patternData.value - 1);
      const patternAddMax = workingMax * (patternData.value - 1);
      workingMin += patternAddMin;
      workingMax += patternAddMax;
      adjustments.push({ label: `Pattern (${patternData.label})`, value: patternAddMin, percent: `+${Math.round((patternData.value - 1) * 100)}%` });
    }
    
    // Fabric type multiplier
    const fabricTypeData = FABRIC_TYPE_MULTIPLIERS[fabricType];
    if (fabricTypeData.value > 1) {
      const typeAddMin = workingMin * (fabricTypeData.value - 1);
      const typeAddMax = workingMax * (fabricTypeData.value - 1);
      workingMin += typeAddMin;
      workingMax += typeAddMax;
      adjustments.push({ label: fabricTypeData.label, value: typeAddMin, percent: `+${Math.round((fabricTypeData.value - 1) * 100)}%` });
    }
    
    // Fabric width adjustment
    const widthData = WIDTH_ADJUSTMENTS[fabricWidth];
    if (widthData.multiplier !== 1) {
      const widthAdjMin = workingMin * (widthData.multiplier - 1);
      const widthAdjMax = workingMax * (widthData.multiplier - 1);
      workingMin += widthAdjMin;
      workingMax += widthAdjMax;
      if (widthData.multiplier > 1) {
        adjustments.push({ label: `Narrow Width (${fabricWidth}cm)`, value: widthAdjMin, percent: `+${Math.round((widthData.multiplier - 1) * 100)}%` });
      } else {
        adjustments.push({ label: `Wide Width (${fabricWidth}cm)`, value: widthAdjMin, percent: `${Math.round((widthData.multiplier - 1) * 100)}%` });
      }
    }
    
    // Body shape modifier
    const bodyData = BODY_SHAPES[bodyShape];
    let bodyAdd = 0;
    if (["suit", "jacket", "coat"].includes(category)) {
      bodyAdd += bodyData.jacket;
    }
    if (["suit", "trousers"].includes(category)) {
      bodyAdd += bodyData.trousers;
    }
    if (bodyAdd > 0) {
      workingMin += bodyAdd;
      workingMax += bodyAdd;
      adjustments.push({ label: `Body Shape (${bodyData.label})`, value: bodyAdd });
    }
    
    // Shrinkage allowance (+4%)
    if (shrinkageAllowance) {
      const shrinkMin = workingMin * 0.04;
      const shrinkMax = workingMax * 0.04;
      workingMin += shrinkMin;
      workingMax += shrinkMax;
      adjustments.push({ label: "Shrinkage Allowance", value: shrinkMin, percent: "+4%" });
    }
    
    // Safety mode (+0.25m)
    if (safetyMode) {
      workingMin += 0.25;
      workingMax += 0.25;
      adjustments.push({ label: "Master Tailor Safety", value: 0.25 });
    }
    
    // Round to nearest 0.05
    const finalMin = Math.ceil(workingMin * 20) / 20;
    const finalMax = Math.ceil(workingMax * 20) / 20;
    const recommended = Math.ceil(((finalMin + finalMax) / 2) * 20) / 20;
    
    return {
      garmentName: garmentData.name,
      category,
      heightCategory,
      heightCm: heightCmVal,
      baseMin,
      baseMax,
      adjustments,
      finalMin,
      finalMax,
      recommended,
      isComplex: adjustments.length > 3,
    };
  }, [garmentType, heightUnit, heightFeet, heightInches, heightCm, jacketLength, breastStyle, 
      trouserRise, trouserLeg, trouserPleats, trouserCuffs, vestBack, coatLength,
      pattern, fabricType, fabricWidth, bodyShape, safetyMode, shrinkageAllowance]);

  // Use estimate in cost calculator
  const handleUseEstimate = () => {
    if (fabricCalculation) {
      setFabricMeters(fabricCalculation.recommended.toString());
      const costSection = document.getElementById('cost-calculator-section');
      if (costSection) {
        costSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      if (onEstimateComplete) onEstimateComplete(fabricCalculation.recommended);
    }
  };

  // Quick search results
  const quickSearchResults = useMemo(() => {
    if (!quickSearchCode || quickSearchCode.length < 2) return [];
    const search = quickSearchCode.toLowerCase();
    const results = [];
    ['suits', 'overcoats', 'shirts', 'linings'].forEach(cat => {
      if (FABRIC_DATA[cat]) {
        FABRIC_DATA[cat].forEach(fabric => {
          if (fabric.rc_code.toLowerCase().includes(search) ||
              fabric.composition?.toLowerCase().includes(search) ||
              fabric.color?.toLowerCase().includes(search)) {
            results.push({ ...fabric, category: cat });
          }
        });
      }
    });
    return results;
  }, [quickSearchCode, FABRIC_DATA]);

  // Cost calculation
  const selectedCMT = CMT_COSTS[cmtGarmentType]?.cmt || 0;
  const selectedCMTName = CMT_COSTS[cmtGarmentType]?.name || "Select a garment";

  const costCalculation = useMemo(() => {
    const fabric = (parseFloat(fabricCost) || 0) * (parseFloat(fabricMeters) || 0);
    const cmt = selectedCMT;
    const freight = FREIGHT_COST;
    const tariff = TARIFF_COST;
    const extrasTotal = extras.reduce((sum, e) => sum + (parseFloat(e.cost) || 0), 0);
    const subtotalBeforeBankFee = fabric + cmt + freight + tariff + extrasTotal;
    const bankFee = (cmt + freight) * BANK_FEE_RATE;
    const subtotal = subtotalBeforeBankFee + bankFee;
    const markupMultiplier = parseFloat(markup) || 2.5;
    const total = subtotal * markupMultiplier;
    const profit = total - subtotal;
    const margin = total > 0 ? (profit / total * 100) : 0;
    return { fabric, cmt, freight, tariff, bankFee, extrasTotal, subtotal, total, profit, margin };
  }, [fabricCost, fabricMeters, selectedCMT, extras, markup]);

  // Fabric catalog helpers
  const filterOptions = useMemo(() => ({
    compositions: getUniqueValues(FABRIC_DATA, fabricCategory, "composition"),
    patterns: getUniqueValues(FABRIC_DATA, fabricCategory, "pattern"),
    colors: getUniqueValues(FABRIC_DATA, fabricCategory, "color"),
  }), [fabricCategory, FABRIC_DATA]);

  const filteredFabrics = useMemo(() => {
    if (!FABRIC_DATA[fabricCategory]) return [];
    return FABRIC_DATA[fabricCategory].filter(fabric => {
      if (fabricSearch) {
        const search = fabricSearch.toLowerCase();
        const matchesSearch = fabric.rc_code.toLowerCase().includes(search) ||
          fabric.composition.toLowerCase().includes(search) ||
          fabric.color.toLowerCase().includes(search);
        if (!matchesSearch) return false;
      }
      if (fabricFilters.composition !== "all" && fabric.composition !== fabricFilters.composition) return false;
      if (fabricFilters.color !== "all" && fabric.color !== fabricFilters.color) return false;
      return true;
    });
  }, [fabricCategory, fabricSearch, fabricFilters, FABRIC_DATA]);

  const handleSelectFabric = (fabric) => {
    setSelectedFabric(fabric);
    if (fabric.price) setFabricCost(fabric.price.toString());
    setShowFabricDialog(false);
  };

  const clearSelectedFabric = () => { setSelectedFabric(null); setFabricCost(""); };
  // Stable IDs so React reuses input DOM nodes correctly when items shift
  // (otherwise removing extras[0] makes extras[1]'s input inherit the old node).
  const addExtra = () => setExtras([...extras, { _id: `x-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, name: "", cost: "" }]);
  const removeExtra = (index) => setExtras(extras.filter((_, i) => i !== index));
  const updateExtra = (index, field, value) => {
    const newExtras = [...extras];
    newExtras[index][field] = value;
    setExtras(newExtras);
  };

  const handleSavePrice = () => {
    if (onPriceCalculated && costCalculation.total > 0) {
      onPriceCalculated(costCalculation.total);
    }
  };

  // Determine which options to show based on garment type
  const garmentData = BASE_FABRIC[garmentType];
  const category = garmentData?.category;
  const showJacketOptions = ["suit", "jacket"].includes(category);
  const showTrouserOptions = ["suit", "trousers"].includes(category);
  const showVestOptions = garmentType === "vest";
  const showCoatOptions = garmentType === "overcoat";

  return (
    <div className="space-y-6">
      {/* ============================================ */}
      {/* SECTION 1: PROFESSIONAL FABRIC ESTIMATOR */}
      {/* ============================================ */}
      <div className="bg-card border border-white/10 p-6 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-serif text-lg flex items-center gap-2">
            <Ruler size={20} className="text-purple-400" />
            Fabric Estimator
            <span className="text-xs px-2 py-0.5 rounded bg-purple-500/20 text-purple-400 ml-2">Professional</span>
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSafetyMode(!safetyMode)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs transition-colors ${
                safetyMode ? "bg-amber-500/20 text-amber-400 border border-amber-500/30" : "bg-white/5 text-slate-400 border border-white/10"
              }`}
            >
              <Shield size={14} />
              Safety Mode {safetyMode ? "ON" : "OFF"}
            </button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mb-4">Pattern-based calculations with design adjustments • Outputs safe cutting range</p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* LEFT - INPUTS */}
          <div className="space-y-4">
            {/* Garment Type */}
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Garment Type</label>
              <Select value={garmentType} onValueChange={setGarmentType}>
                <SelectTrigger className="bg-background border-white/10"><SelectValue placeholder="Select garment" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="2pc-suit">2-Piece Suit</SelectItem>
                  <SelectItem value="3pc-suit">3-Piece Suit</SelectItem>
                  <SelectItem value="jacket">Jacket Only</SelectItem>
                  <SelectItem value="trousers">Trousers Only</SelectItem>
                  <SelectItem value="vest">Vest / Waistcoat</SelectItem>
                  <SelectItem value="overcoat">Overcoat</SelectItem>
                  <SelectItem value="shirt">Shirt</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Height */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm text-muted-foreground">Height</label>
                <div className="flex gap-1">
                  <button onClick={() => setHeightUnit("imperial")} className={`text-xs px-2 py-1 rounded ${heightUnit === "imperial" ? "bg-primary text-primary-foreground" : "bg-white/5"}`}>ft/in</button>
                  <button onClick={() => setHeightUnit("metric")} className={`text-xs px-2 py-1 rounded ${heightUnit === "metric" ? "bg-primary text-primary-foreground" : "bg-white/5"}`}>cm</button>
                </div>
              </div>
              {heightUnit === "imperial" ? (
                <div className="flex gap-2">
                  <Input type="number" placeholder="Feet" value={heightFeet} onChange={(e) => setHeightFeet(e.target.value)} className="flex-1 bg-background border-white/10" />
                  <Input type="number" placeholder="Inches" value={heightInches} onChange={(e) => setHeightInches(e.target.value)} className="flex-1 bg-background border-white/10" />
                </div>
              ) : (
                <Input type="number" placeholder="Height in cm" value={heightCm} onChange={(e) => setHeightCm(e.target.value)} className="bg-background border-white/10" />
              )}
            </div>

            {/* Body Shape */}
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Body Build</label>
              <div className="grid grid-cols-5 gap-1">
                {Object.entries(BODY_SHAPES).map(([key, data]) => (
                  <button
                    key={key}
                    onClick={() => setBodyShape(key)}
                    className={`px-2 py-2 rounded text-xs transition-colors ${
                      bodyShape === key ? "bg-purple-500/20 text-purple-400 border border-purple-500/30" : "bg-white/5 border border-white/10 hover:border-white/20"
                    }`}
                  >
                    {data.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Jacket Options */}
            {showJacketOptions && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Breast Style</label>
                  <Select value={breastStyle} onValueChange={setBreastStyle}>
                    <SelectTrigger className="bg-background border-white/10 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Single Breasted</SelectItem>
                      <SelectItem value="double">Double Breasted (+0.25m)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Jacket Length</label>
                  <Select value={jacketLength} onValueChange={setJacketLength}>
                    <SelectTrigger className="bg-background border-white/10 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="short">Short</SelectItem>
                      <SelectItem value="regular">Regular</SelectItem>
                      <SelectItem value="long">Long (+0.25m)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Trouser Options */}
            {showTrouserOptions && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Trouser Rise</label>
                  <Select value={trouserRise} onValueChange={setTrouserRise}>
                    <SelectTrigger className="bg-background border-white/10 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low Rise</SelectItem>
                      <SelectItem value="regular">Regular Rise</SelectItem>
                      <SelectItem value="high">High Rise (+0.12m)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Leg Style</label>
                  <Select value={trouserLeg} onValueChange={setTrouserLeg}>
                    <SelectTrigger className="bg-background border-white/10 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="slim">Slim</SelectItem>
                      <SelectItem value="classic">Classic</SelectItem>
                      <SelectItem value="wide">Wide (+0.10m)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Pleats</label>
                  <Select value={trouserPleats} onValueChange={setTrouserPleats}>
                    <SelectTrigger className="bg-background border-white/10 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="flat">Flat Front</SelectItem>
                      <SelectItem value="pleated">Pleated (+0.15m)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Cuffs</label>
                  <Select value={trouserCuffs} onValueChange={setTrouserCuffs}>
                    <SelectTrigger className="bg-background border-white/10 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no">No Cuffs</SelectItem>
                      <SelectItem value="yes">Cuffed (+0.08m)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Vest Options */}
            {showVestOptions && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Vest Back</label>
                  <Select value={vestBack} onValueChange={setVestBack}>
                    <SelectTrigger className="bg-background border-white/10 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lining">Lining Back (0.40-0.45m)</SelectItem>
                      <SelectItem value="self">Self Back (0.60-0.70m)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Breast Style</label>
                  <Select value={breastStyle} onValueChange={setBreastStyle}>
                    <SelectTrigger className="bg-background border-white/10 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Single Breasted</SelectItem>
                      <SelectItem value="double">Double Breasted (+0.10m)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Coat Options */}
            {showCoatOptions && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Coat Length</label>
                  <Select value={coatLength} onValueChange={setCoatLength}>
                    <SelectTrigger className="bg-background border-white/10 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="short">Short / Topcoat</SelectItem>
                      <SelectItem value="knee">Knee-Length</SelectItem>
                      <SelectItem value="full">Full-Length</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Breast Style</label>
                  <Select value={breastStyle} onValueChange={setBreastStyle}>
                    <SelectTrigger className="bg-background border-white/10 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Single Breasted</SelectItem>
                      <SelectItem value="double">Double Breasted (+0.25m)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Fabric Options */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Pattern</label>
                <Select value={pattern} onValueChange={setPattern}>
                  <SelectTrigger className="bg-background border-white/10 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="solid">Solid</SelectItem>
                    <SelectItem value="subtleStripe">Subtle Stripe (+8%)</SelectItem>
                    <SelectItem value="stripe">Bold Stripe (+12%)</SelectItem>
                    <SelectItem value="check">Check (+18%)</SelectItem>
                    <SelectItem value="largeCheck">Large Check (+25%)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Fabric Type</label>
                <Select value={fabricType} onValueChange={setFabricType}>
                  <SelectTrigger className="bg-background border-white/10 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="nap">Nap/Velvet (+12%)</SelectItem>
                    <SelectItem value="delicate">Delicate/Silk (+8%)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Fabric Width</label>
                <Select value={fabricWidth} onValueChange={setFabricWidth}>
                  <SelectTrigger className="bg-background border-white/10 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="140">140cm (+15%)</SelectItem>
                    <SelectItem value="150">150cm (Standard)</SelectItem>
                    <SelectItem value="160">160cm (-8%)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Toggles */}
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={shrinkageAllowance}
                  onChange={(e) => setShrinkageAllowance(e.target.checked)}
                  className="rounded border-white/20 bg-white/5 text-purple-500"
                />
                <span className="text-muted-foreground">Shrinkage Allowance (+4%)</span>
              </label>
            </div>
          </div>

          {/* RIGHT - RESULT */}
          <div>
            {fabricCalculation ? (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-purple-500/5 border border-purple-500/20 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle size={18} className="text-green-400" />
                    <span className="font-medium">{fabricCalculation.garmentName}</span>
                  </div>
                  {fabricCalculation.heightCm > 0 && (
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      fabricCalculation.heightCategory === "tall" ? "bg-orange-500/20 text-orange-400" :
                      fabricCalculation.heightCategory === "short" ? "bg-blue-500/20 text-blue-400" :
                      "bg-green-500/20 text-green-400"
                    }`}>
                      {fabricCalculation.heightCategory.charAt(0).toUpperCase() + fabricCalculation.heightCategory.slice(1)} ({Math.round(fabricCalculation.heightCm)}cm)
                    </span>
                  )}
                </div>

                {/* Adjustments breakdown */}
                <div className="space-y-1.5 text-sm mb-4">
                  <div className="flex justify-between text-muted-foreground">
                    <span>Base Range</span>
                    <span>{fabricCalculation.baseMin.toFixed(2)}–{fabricCalculation.baseMax.toFixed(2)}m</span>
                  </div>
                  {fabricCalculation.adjustments.map((adj, idx) => (
                    <div key={idx} className="flex justify-between">
                      <span className="text-muted-foreground">{adj.label}</span>
                      <span className={adj.value > 0 ? "text-orange-400" : "text-green-400"}>
                        {adj.percent || `${adj.value > 0 ? "+" : ""}${adj.value.toFixed(2)}m`}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Final result - RANGE */}
                <div className="border-t border-purple-500/20 pt-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Recommended</span>
                    <span className="text-2xl font-serif text-purple-400">{fabricCalculation.recommended.toFixed(2)}m</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Scissors size={14} />
                      Safe Cutting Range
                    </span>
                    <span className="text-green-400 font-medium">{fabricCalculation.finalMin.toFixed(2)}–{fabricCalculation.finalMax.toFixed(2)}m</span>
                  </div>
                </div>

                {safetyMode && (
                  <div className="mt-3 p-2 bg-amber-500/10 border border-amber-500/20 rounded text-xs flex items-center gap-2">
                    <Shield size={14} className="text-amber-400" />
                    <span className="text-amber-300">Master Tailor Safety Mode: +0.25m included</span>
                  </div>
                )}

                <Button onClick={handleUseEstimate} className="w-full mt-4 bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 border border-purple-500/30">
                  Use {fabricCalculation.recommended.toFixed(2)}m in Cost Calculator ↓
                </Button>
              </motion.div>
            ) : (
              <div className="bg-white/5 border border-white/10 rounded-lg p-6 text-center">
                <User size={32} className="mx-auto text-muted-foreground mb-3" />
                <p className="text-sm text-muted-foreground">Select garment type to calculate fabric</p>
              </div>
            )}

            {/* Quick Reference */}
            <div className="mt-4 p-3 bg-white/5 border border-white/10 rounded text-xs">
              <p className="font-medium mb-2 flex items-center gap-1"><Info size={12} />Quick Reference (150cm width, solid)</p>
              <div className="grid grid-cols-3 gap-2 text-muted-foreground">
                <div>
                  <p className="text-green-400/80 text-[10px] mb-1">Short ≤170cm</p>
                  <p>2pc: 2.8-3.0m</p>
                  <p>3pc: 3.4-3.7m</p>
                </div>
                <div>
                  <p className="text-blue-400/80 text-[10px] mb-1">Regular 171-182cm</p>
                  <p>2pc: 3.0-3.3m</p>
                  <p>3pc: 3.6-4.0m</p>
                </div>
                <div>
                  <p className="text-orange-400/80 text-[10px] mb-1">Tall ≥183cm</p>
                  <p>2pc: 3.3-3.6m</p>
                  <p>3pc: 4.0-4.4m</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* SECTION 2: GARMENT COST CALCULATOR */}
      {/* ============================================ */}
      <div id="cost-calculator-section" className="bg-card border border-white/10 p-6 rounded-lg">
        <h2 className="font-serif text-lg mb-4 flex items-center gap-2">
          <Calculator size={20} className="text-blue-400" />
          Garment Cost Calculator
        </h2>
        <p className="text-xs text-muted-foreground mb-4">Calculate pricing using Kutesmart CMT costs • RCMTM 2022 Fabric Catalog</p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left - Inputs */}
          <div className="space-y-4">
            {/* CMT Garment Selection */}
            <div className="bg-white/5 border border-white/10 p-4 rounded">
              <label className="text-sm text-muted-foreground mb-2 block">Garment Type (CMT)</label>
              <Select value={cmtGarmentType} onValueChange={setCmtGarmentType}>
                <SelectTrigger className="bg-background border-white/10"><SelectValue placeholder="Select garment type" /></SelectTrigger>
                <SelectContent className="max-h-80">
                  <SelectItem disabled value="header-3pc" className="font-bold text-primary">— Three-piece Suits —</SelectItem>
                  <SelectItem value="full-canvas-3pc">Full Canvas 3pc ($310)</SelectItem>
                  <SelectItem value="half-canvas-3pc">Half Canvas 3pc ($230)</SelectItem>
                  <SelectItem value="fused-canvas-3pc">Fused Canvas 3pc ($210)</SelectItem>
                  <SelectItem value="unstructured-3pc">Unstructured 3pc ($230)</SelectItem>
                  <SelectItem disabled value="header-2pc" className="font-bold text-primary">— Two-piece Suits —</SelectItem>
                  <SelectItem value="full-canvas-2pc">Full Canvas 2pc ($260)</SelectItem>
                  <SelectItem value="half-canvas-2pc">Half Canvas 2pc ($180)</SelectItem>
                  <SelectItem value="fused-canvas-2pc">Fused Canvas 2pc ($160)</SelectItem>
                  <SelectItem value="unstructured-2pc">Unstructured 2pc ($180)</SelectItem>
                  <SelectItem disabled value="header-jacket" className="font-bold text-primary">— Jackets —</SelectItem>
                  <SelectItem value="full-canvas-jacket">Full Canvas Jacket ($210)</SelectItem>
                  <SelectItem value="half-canvas-jacket">Half Canvas Jacket ($130)</SelectItem>
                  <SelectItem value="fused-canvas-jacket">Fused Canvas Jacket ($110)</SelectItem>
                  <SelectItem value="unstructured-jacket">Unstructured Jacket ($135)</SelectItem>
                  <SelectItem disabled value="header-shirt" className="font-bold text-primary">— Shirts —</SelectItem>
                  <SelectItem value="shirt">Shirt ($30)</SelectItem>
                  <SelectItem value="shirt-collar">Shirt + Collar ($40)</SelectItem>
                  <SelectItem value="shirt-cuff">Shirt + Cuff ($40)</SelectItem>
                  <SelectItem value="shirt-pocket">Shirt + Pocket ($40)</SelectItem>
                  <SelectItem disabled value="header-pants" className="font-bold text-primary">— Pants —</SelectItem>
                  <SelectItem value="pant">Pant ($50)</SelectItem>
                  <SelectItem value="dress-chino">Dress Chino ($50)</SelectItem>
                  <SelectItem value="bermuda">Bermuda ($50)</SelectItem>
                  <SelectItem value="jeans">Jeans ($40)</SelectItem>
                  <SelectItem value="casual-chinos">Casual Chinos ($40)</SelectItem>
                  <SelectItem disabled value="header-other" className="font-bold text-primary">— Other —</SelectItem>
                  <SelectItem value="vest">Vest ($50)</SelectItem>
                  <SelectItem value="full-canvas-overcoat">Full Canvas Overcoat ($250)</SelectItem>
                  <SelectItem value="half-canvas-overcoat">Half Canvas Overcoat ($180)</SelectItem>
                  <SelectItem value="fused-canvas-overcoat">Fused Canvas Overcoat ($150)</SelectItem>
                  <SelectItem value="handkerchief">Handkerchief ($10)</SelectItem>
                </SelectContent>
              </Select>
              {cmtGarmentType && <p className="text-xs mt-2 text-primary">CMT Cost: <span className="font-bold">${selectedCMT}</span></p>}
            </div>

            {/* Fabric Cost */}
            <div className="bg-white/5 border border-white/10 p-4 rounded">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm text-muted-foreground flex items-center gap-2"><Layers size={16} className="text-blue-400" />Fabric Cost</label>
              </div>

              {selectedFabric && (
                <div className="mb-3 p-2 bg-blue-500/10 border border-blue-500/30 rounded flex items-start justify-between">
                  <div><p className="text-xs font-mono text-blue-400">{selectedFabric.rc_code}</p><p className="text-[10px] text-muted-foreground">{selectedFabric.composition} • {selectedFabric.color}</p></div>
                  <button onClick={clearSelectedFabric} className="text-muted-foreground hover:text-foreground"><X size={14} /></button>
                </div>
              )}

              {/* Quick Search Code */}
              <div className="mb-3">
                <label className="text-xs text-muted-foreground mb-1 block">Search Code</label>
                <div className="relative">
                  <Search size={12} className="absolute left-2 top-2.5 text-muted-foreground" />
                  <Input 
                    type="text" 
                    placeholder="Enter fabric code (e.g., RC-1234)" 
                    value={quickSearchCode}
                    onChange={(e) => setQuickSearchCode(e.target.value)}
                    className="bg-background border-white/10 pl-7 h-9 text-sm"
                  />
                  {quickSearchCode && (
                    <button onClick={() => setQuickSearchCode("")} className="absolute right-2 top-2.5 text-muted-foreground hover:text-foreground"><X size={12} /></button>
                  )}
                </div>
                {quickSearchCode && quickSearchResults.length > 0 && (
                  <div className="mt-2 max-h-32 overflow-y-auto border border-white/10 rounded bg-background">
                    {quickSearchResults.slice(0, 5).map((fabric, idx) => (
                      <div key={`quick-${fabric.rc_code}-${idx}`} onClick={() => { handleSelectFabric(fabric); setQuickSearchCode(""); }}
                        className="px-3 py-2 hover:bg-blue-500/10 cursor-pointer border-b border-white/5 last:border-0">
                        <p className="text-xs font-mono text-blue-400">{fabric.rc_code}</p>
                        <p className="text-[10px] text-muted-foreground">{fabric.composition} • {fabric.color} {fabric.price ? `• $${fabric.price}/m` : ''}</p>
                      </div>
                    ))}
                  </div>
                )}
                {quickSearchCode && quickSearchResults.length === 0 && (
                  <p className="text-xs text-muted-foreground mt-1">No fabrics found</p>
                )}
              </div>

              <Button variant="outline" onClick={() => setShowFabricDialog(true)} className="w-full mb-3 border-blue-500/30 text-blue-400 hover:bg-blue-500/10" size="sm">
                <Search size={14} className="mr-2" />Browse RCMTM Fabric Catalog
              </Button>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Cost per meter</label>
                  <div className="relative">
                    <DollarSign size={12} className="absolute left-2 top-2.5 text-muted-foreground" />
                    <Input type="number" placeholder="0.00" value={fabricCost} onChange={(e) => { setFabricCost(e.target.value); if (selectedFabric) setSelectedFabric(null); }} className="bg-background border-white/10 pl-6 h-9" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block flex items-center gap-2">
                    Meters needed
                    {fabricMeters && parseFloat(fabricMeters) > 0 && (
                      <span className="text-[10px] bg-purple-500/20 text-purple-400 px-1.5 py-0.5 rounded">from estimator</span>
                    )}
                  </label>
                  <Input type="number" placeholder="0" value={fabricMeters} onChange={(e) => setFabricMeters(e.target.value)} 
                    className={`bg-background border-white/10 h-9 ${fabricMeters && parseFloat(fabricMeters) > 0 ? 'border-purple-500/50 ring-1 ring-purple-500/30' : ''}`} />
                </div>
              </div>

              <div className="mt-3">
                <label className="text-xs text-muted-foreground mb-1 block">Markup Multiplier</label>
                <Select value={markup} onValueChange={setMarkup}>
                  <SelectTrigger className="bg-background border-white/10 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1.5">1.5x (50% markup)</SelectItem>
                    <SelectItem value="2.0">2.0x (100% markup)</SelectItem>
                    <SelectItem value="2.5">2.5x (150% markup)</SelectItem>
                    <SelectItem value="3.0">3.0x (200% markup)</SelectItem>
                    <SelectItem value="3.5">3.5x (250% markup)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Additional Costs */}
            <div className="bg-white/5 border border-white/10 p-4 rounded">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm text-muted-foreground">Additional Costs</label>
                <Button variant="outline" size="sm" onClick={addExtra} className="h-7 text-xs border-white/20"><Plus size={12} className="mr-1" />Add</Button>
              </div>
              {extras.length === 0 ? (
                <p className="text-xs text-muted-foreground">No additional costs added</p>
              ) : (
                <div className="space-y-2">
                  {extras.map((extra, index) => (
                    <div key={extra._id || `extra-${index}`} className="flex gap-2">
                      <Input placeholder="Description" value={extra.name} onChange={(e) => updateExtra(index, "name", e.target.value)} className="bg-background border-white/10 flex-1 h-8 text-sm" />
                      <div className="relative w-20">
                        <DollarSign size={12} className="absolute left-2 top-2 text-muted-foreground" />
                        <Input type="number" placeholder="0" value={extra.cost} onChange={(e) => updateExtra(index, "cost", e.target.value)} className="bg-background border-white/10 pl-6 h-8 text-sm" />
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => removeExtra(index)} className="text-red-400 hover:text-red-500 h-8 w-8"><Minus size={14} /></Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right - Cost Breakdown */}
          <div className="bg-white/5 border border-white/10 p-4 rounded">
            <h3 className="font-serif text-base mb-4 flex items-center gap-2"><DollarSign size={18} className="text-primary" />Cost Breakdown</h3>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">CMT Cost ({selectedCMTName})</span><span>${costCalculation.cmt.toFixed(2)}</span></div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Fabric ({fabricMeters || 0}m × ${fabricCost || 0}){selectedFabric && <span className="text-blue-400 ml-1 text-xs">• {selectedFabric.rc_code}</span>}</span>
                <span>${costCalculation.fabric.toFixed(2)}</span>
              </div>
              <div className="flex justify-between"><span className="text-muted-foreground">Freight</span><span>${costCalculation.freight.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Tariff</span><span>${costCalculation.tariff.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Bank Fee (3% of CMT + Freight)</span><span>${costCalculation.bankFee.toFixed(2)}</span></div>
              {extras.length > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Additional Costs</span><span>${costCalculation.extrasTotal.toFixed(2)}</span></div>}

              <div className="border-t border-white/10 pt-3 mt-3">
                <div className="flex justify-between font-medium"><span>Subtotal (Your Cost)</span><span>${costCalculation.subtotal.toFixed(2)}</span></div>
              </div>

              <div className="flex justify-between text-muted-foreground"><span>Markup ({markup}x)</span><span>× {markup}</span></div>

              <div className="border-t border-primary pt-4 mt-4">
                <div className="flex justify-between text-xl font-serif"><span>Sell Price</span><span className="text-primary">${costCalculation.total.toFixed(2)}</span></div>
              </div>

              <div className="mt-4 p-3 bg-primary/5 border border-primary/20 rounded space-y-1">
                <p className="text-xs"><strong className="text-foreground">Profit:</strong> <span className="text-green-400">${costCalculation.profit.toFixed(2)}</span></p>
                <p className="text-xs"><strong className="text-foreground">Margin:</strong> <span className="text-green-400">{costCalculation.margin.toFixed(1)}%</span></p>
              </div>

              {onPriceCalculated && costCalculation.total > 0 && (
                <Button onClick={handleSavePrice} className="w-full mt-4 bg-primary text-primary-foreground hover:bg-primary/90">
                  Save ${costCalculation.total.toFixed(2)} to Invoice
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Fabric Catalog Dialog */}
      <Dialog open={showFabricDialog} onOpenChange={setShowFabricDialog}>
        <DialogContent className="bg-card border-white/10 max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl flex items-center gap-2"><Layers size={20} className="text-blue-400" />RCMTM 2022 Fabric Catalog</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden flex flex-col">
            <div className="flex gap-2 mb-4 flex-wrap">
              {[
                { key: "suits", label: "Suits", icon: Package, count: FABRIC_DATA.suits?.length || 0 },
                { key: "overcoats", label: "Overcoats", icon: Package, count: FABRIC_DATA.overcoats?.length || 0 },
                { key: "shirts", label: "Shirts", icon: Shirt, count: FABRIC_DATA.shirts?.length || 0 },
                { key: "linings", label: "Linings", icon: Package, count: FABRIC_DATA.linings?.length || 0 }
              ].map(cat => (
                <button key={cat.key} onClick={() => { setFabricCategory(cat.key); setFabricFilters({ composition: "all", pattern: "all", color: "all" }); setFabricSearch(""); }}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm transition-colors ${fabricCategory === cat.key ? "bg-blue-500/20 text-blue-400 border border-blue-500/30" : "bg-white/5 text-muted-foreground hover:bg-white/10"}`}>
                  <cat.icon size={14} /><span>{cat.label}</span><span className="text-xs opacity-60">({cat.count})</span>
                </button>
              ))}
            </div>

            <div className="flex gap-2 mb-4">
              <div className="relative flex-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input placeholder="Search by code, composition, color..." value={fabricSearch} onChange={(e) => setFabricSearch(e.target.value)} className="pl-8 bg-background border-white/10 h-9" />
              </div>
              <Select value={fabricFilters.composition} onValueChange={(v) => setFabricFilters(prev => ({ ...prev, composition: v }))}>
                <SelectTrigger className="w-32 bg-background border-white/10 h-9"><SelectValue placeholder="Composition" /></SelectTrigger>
                <SelectContent><SelectItem value="all">All</SelectItem>{filterOptions.compositions.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={fabricFilters.color} onValueChange={(v) => setFabricFilters(prev => ({ ...prev, color: v }))}>
                <SelectTrigger className="w-28 bg-background border-white/10 h-9"><SelectValue placeholder="Color" /></SelectTrigger>
                <SelectContent><SelectItem value="all">All</SelectItem>{filterOptions.colors.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <div className="flex-1 overflow-y-auto space-y-2 pr-2">
              {filteredFabrics.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">No fabrics found</div>
              ) : (
                filteredFabrics.slice(0, 100).map((fabric, idx) => (
                  <div key={`${fabric.rc_code}-${idx}`} onClick={() => handleSelectFabric(fabric)} className="p-3 bg-white/5 border border-white/10 rounded hover:border-blue-500/50 hover:bg-blue-500/5 cursor-pointer transition-colors">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-mono text-sm text-blue-400">{fabric.rc_code}</p>
                        <p className="text-xs text-muted-foreground mt-1">{fabric.composition} • {fabric.pattern} • {fabric.color}</p>
                        {fabric.yarn && <p className="text-xs text-muted-foreground">{fabric.yarn}</p>}
                      </div>
                      {fabric.price && <p className="font-medium text-primary">${fabric.price}/m</p>}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FabricEstimator;
